<template>
    <form-wrapper @submit="handleFormSubmission()">
        <complete-physician-profile></complete-physician-profile>

        <common-page-header
            :heading="$t('pages.auth.signup.steps.register_physicians.register_subheader')"
            size="small"
        ></common-page-header>

        <form-row>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.auth.register.fields.first_name.label')"
                    :placeholder="$t('forms.auth.register.fields.first_name.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="firstName"
                    @change="change($event)"
                ></text-field>
            </form-cell>

            <form-cell width="quarter">
                <text-field
                    :label="$t('forms.auth.register.fields.middle_name.shortLabel')"
                    :placeholder="$t('forms.auth.register.fields.middle_name.placeholder')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="middleName"
                    @change="change($event)"
                ></text-field>
            </form-cell>

            <form-cell width="half">
                <text-field
                    :label="$t('forms.auth.register.fields.last_name.label')"
                    :placeholder="$t('forms.auth.register.fields.last_name.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="lastName"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <text-field
                    :label="$t('forms.auth.register.fields.email.label')"
                    :placeholder="$t('forms.auth.register.fields.email.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="email"
                    type="email"
                    @change="change($event)"
                >
                    <template #hint>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <p v-html="$t('forms.auth.email_hint')"></p>
                    </template>

                    <template #error="{ error }">
                        <template v-if="error.code === 'entity.duplicate'">
                            {{ $t('forms.auth.register.errors.email.duplicate.message') }}
                            <router-link :to="{ name: 'auth.login' }">
                                {{ $t('forms.auth.register.errors.email.duplicate.link') }}
                            </router-link>
                        </template>
                        <template v-else>
                            {{ error }}
                        </template>
                    </template>
                </text-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <phone-field
                    :label="$t('forms.auth.register.fields.phone.label')"
                    :placeholder="$t('common.phone_number.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="phone"
                    @change="change($event)"
                    @country-code-change="prefillData($event)"
                ></phone-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="third">
                <select-field
                    :label="$t('forms.auth.register.fields.title.label')"
                    :placeholder="$t('forms.auth.register.fields.title.placeholder')"
                    :options="designationOptions"
                    :isRequired="true"
                    :formData="data"
                    valuePath="designation"
                    @change="change($event)"
                ></select-field>
            </form-cell>

            <form-cell>
                <multi-select-field
                    :label="$t('forms.auth.register.fields.specialty.label')"
                    :placeholder="$t('forms.auth.register.fields.specialty.placeholder')"
                    :isRequired="true"
                    :formData="data"
                    :options="specialities"
                    :error="specialitiesError"
                    :expandableOptions="true"
                    :oneOptionSelected="true"
                    :grayColor="true"
                    valuePath="specialities"
                    @change="specialtyChanged($event)"
                ></multi-select-field>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons>
                <common-button :isProcessing="isSending" :label="$t('common.microcopy.next')" type="primary"></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import CompletePhysicianProfile from 'components/page-elements/auth/register-physicians/CompletePhysicianProfile'
    import UserRolesEnum from '../../../enums/UserRolesEnum'
    import UserDesignationEnum from '../../../enums/UserDesignationEnum'
    import PracticesAPI from 'api/PracticesAPI'
    import UsersAPI from 'api/UsersAPI'

    export default {
        mixins: [FormMixin],

        components: {
            CompletePhysicianProfile,
        },

        props: {
            specialities: { type: Array, required: true },
        },

        data() {
            return {
                specialitiesError: '',
                designationOptions: this.loadDesignationOptions(),
            }
        },

        computed: {
            subSpecialities() {
                const merged = [].concat.apply(
                    [],
                    this.specialities.map((speciality) => speciality.options),
                )
                return merged.map((subSpeciality) => ({
                    specialityId: subSpeciality.data.optionId,
                    subSpecialityId: subSpeciality.data.id,
                }))
            },
        },

        methods: {
            checkSpecialtySelected() {
                if (this.data.specialities && this.data.specialities.length) {
                    this.specialitiesError = ''
                    return true
                }
                this.specialitiesError = this.$t('forms.auth.register.errors.specialty')
                return false
            },

            handleFormSubmission() {
                if (this.checkSpecialtySelected()) {
                    const data = {
                        ...this.data,
                        practiceId: this.$auth.getPractice().id,
                        userRoleId: UserRolesEnum.PHYSICIAN.ID,
                        userSpecialities: this.subSpecialities.filter((subSpeciality) =>
                            this.data.specialities.includes(subSpeciality.subSpecialityId),
                        ),
                    }
                    this.registerPhysician(data).then((res) => {
                        this.$auth.refreshPracticeMembers().then(() => {
                            this.$auth.setActiveMember({ id: res.id })
                            if (
                                process.env.VUE_APP_ENV === 'production' &&
                                !this.$auth.getActiveMember().isNotificationSentOnRegister
                            ) {
                                UsersAPI.sendNewMemberRegisteredNotificationEmail(res.id)
                            }
                            this.$router.go()
                        })
                    })
                }
            },

            registerPhysician(data) {
                return this.handleRequest(PracticesAPI.createMember(data))
            },

            specialtyChanged(event) {
                this.specialitiesError = ''
                this.change(event)
            },

            loadDesignationOptions() {
                return Object.values(UserDesignationEnum).map((designation) => ({ value: designation, label: designation }))
            },
        },
    }
</script>
